import { useFormik } from 'formik';
import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import { ReturnFilterStore } from 'zustand-store';
import { ReactTag, ReturnSearchForm, ReturnSearchType } from 'types';
import { removeLocalStorageItem } from 'helpers';
import {
  LocalStorageKeys,
  TEST_IDS,
  getDefaultReturnSearch,
  returnSearchOptions,
} from 'commons';
import { Input, Select } from '../components';
import * as S from '../styles';

const DEFAULT_SEARCH_TYPE = 'codes';

export function ReturnSearch() {
  const { returnSearch, setReturnSearch } = ReturnFilterStore.useStore();

  const [inputText, setInputText] = useState<string>('');
  const [tags, setTags] = useState<ReactTag[]>([]);

  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      const { searchType } = returnSearch;

      if (searchType === DEFAULT_SEARCH_TYPE) {
        const parseToTags = returnSearch[searchType]
          .split(',')
          .filter((value) => value !== '');

        if (parseToTags.length) {
          const initialTags: ReactTag[] = parseToTags.map((code) => {
            return {
              id: code,
              text: code,
            };
          });

          setTags(initialTags);
        }
      }

      firstRender.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender]);

  const { values, handleSubmit, setFieldValue, setValues } =
    useFormik<ReturnSearchForm>({
      initialValues: returnSearch,

      onSubmit: (values) => {
        const searchTags: ReactTag[] = tags;
        const { searchType, cpf, customerName } = values;

        if (inputText !== '') {
          const closedTag: ReactTag = { id: inputText, text: inputText };
          setInputText('');

          searchTags.push(closedTag);
          setTags(searchTags);
        }

        const formattedCodes = searchTags.map((tag) => tag.text).join(',');
        const submitCodes = formattedCodes;

        const submitSearch: ReturnSearchForm = {
          searchType: searchType,
          codes: searchType === ReturnSearchType.Codes ? submitCodes : '',
          cpf: searchType === ReturnSearchType.Cpf ? cpf : '',
          customerName:
            searchType === ReturnSearchType.CustomerName ? customerName : '',
          filterDate: new Date().toDateString(),
        };

        setReturnSearch(submitSearch);
      },

      onReset: (_, { setValues }) => {
        removeLocalStorageItem(LocalStorageKeys.ReturnSearch);

        const defaultReturnSearch = getDefaultReturnSearch();
        setReturnSearch(defaultReturnSearch);
        setValues(defaultReturnSearch);
        setTags([]);
        setInputText('');
      },
    });

  useEffect(() => {
    if (values.filterDate !== returnSearch.filterDate) {
      setValues(returnSearch);
      setTags([]);
      setInputText('');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnSearch.filterDate]);

  useEffect(() => {
    const defaultReturnSearch = getDefaultReturnSearch();

    if (isEqual(defaultReturnSearch, returnSearch)) {
      const { codes, cpf, searchType } = values;
      const isSearchModified =
        tags.length ||
        inputText ||
        codes ||
        cpf ||
        searchType !== DEFAULT_SEARCH_TYPE;

      if (isSearchModified) {
        setValues(defaultReturnSearch);
        setTags([]);
        setInputText('');
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnSearch]);

  function changeSearchType(value: string) {
    setTags([]);
    setInputText('');
    setFieldValue('searchType', value);
    setFieldValue('customerName', '');
    setFieldValue('codes', '');
    setFieldValue('cpf', '');

    handleSubmit();
  }

  const { searchType } = values;
  const hasTag = searchType === DEFAULT_SEARCH_TYPE;

  function changeInputValue(value: string) {
    if (hasTag) {
      setInputText(value);
    } else {
      setFieldValue(searchType, value);
    }
  }

  const inputValue = hasTag ? inputText : values[searchType];

  return (
    <S.Form
      autoComplete="off"
      data-testid={TEST_IDS.CONSIGNMENT_SEARCH_BAR}
      onSubmit={handleSubmit}
    >
      <Select
        id="searchType"
        value={searchType}
        handleChange={changeSearchType}
        options={returnSearchOptions}
      />

      <Input
        id={searchType}
        value={inputValue}
        handleChange={changeInputValue}
        handleSubmit={handleSubmit}
        tags={tags}
        setTags={setTags}
      />

      <S.Button type="submit" aria-label="pesquisar">
        <MdSearch />
      </S.Button>
    </S.Form>
  );
}
